import { useEffect, useRef, useState } from "react";

const CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes
const VERSION_KEY = "app_version";
const VERSION_URL = "/version.json";
const FETCH_TIMEOUT_MS = 8000;

const fetchVersion = async () => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

  try {
    const response = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
      cache: "no-store",
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(`Version request failed: ${response.status}`);
    }

    return response.json();
  } finally {
    clearTimeout(timeoutId);
  }
};

export const useVersionCheck = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const pendingRef = useRef(false);

  useEffect(() => {
    const checkVersion = async () => {
      if (pendingRef.current) return;
      if (navigator && !navigator.onLine) return; // skip while offline

      pendingRef.current = true;

      try {
        const storedVersion = localStorage.getItem(VERSION_KEY);
        const data = await fetchVersion();
        const serverVersion = data?.version;

        if (!serverVersion) return;

        if (!storedVersion) {
          localStorage.setItem(VERSION_KEY, serverVersion);
          return;
        }

        if (serverVersion !== storedVersion) {
          setUpdateAvailable(true);
        }
      } catch (error) {
        // Swallow errors: version checks should never block the app
      } finally {
        pendingRef.current = false;
      }
    };

    checkVersion();
    const interval = setInterval(checkVersion, CHECK_INTERVAL);
    const resumeOnOnline = () => checkVersion();

    window.addEventListener("online", resumeOnOnline);

    return () => {
      clearInterval(interval);
      window.removeEventListener("online", resumeOnOnline);
    };
  }, []);

  const refreshPage = () => {
    fetchVersion()
      .then((data) => {
        if (data?.version) {
          localStorage.setItem(VERSION_KEY, data.version);
        }

        if ("caches" in window) {
          caches.keys().then((names) => {
            names.forEach((name) => caches.delete(name));
          });
        }

        window.location.reload();
      })
      .catch(() => {
        window.location.reload();
      });
  };

  return { updateAvailable, refreshPage };
};
